import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notebooksService } from '@/services/collections/notebooks';
import { useAuth } from '@/contexts/AuthContext';
import type {
  Notebook,
  UpdateNotebookInput,
  CreatePageInput,
  UpdatePageInput,
  InviteToNotebookInput,
} from '@/types/notebook';

export const useNotebook = (id: string) => {
  const { user } = useAuth();
  const [notebook, setNotebook] = useState<Notebook | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  // Debounced page save
  const pendingPageUpdate = useRef<{ pageId: string; data: UpdatePageInput } | null>(null);
  const saveTimeout = useRef<NodeJS.Timeout | null>(null);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!id) return;

    setLoading(true);
    const unsubscribe = notebooksService.subscribe(
      id,
      (nb) => {
        setNotebook(nb);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [id]);

  // Check access permissions
  const access = notebook && user
    ? notebooksService.checkAccess(notebook, user.uid, user.email || '')
    : { hasAccess: false, canEdit: false, isOwner: false };

  // Get sorted pages
  const sortedPages = notebook
    ? [...notebook.pages].sort((a, b) => a.order - b.order)
    : [];

  // Get current page
  const currentPage = sortedPages[currentPageIndex] || null;

  // Flush pending updates on unmount
  useEffect(() => {
    return () => {
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
      const pending = pendingPageUpdate.current;
      if (pending && id) {
        notebooksService.updatePage(id, pending.pageId, pending.data).catch(console.error);
      }
    };
  }, [id]);

  // Update notebook metadata
  const updateNotebook = useCallback(
    async (data: UpdateNotebookInput) => {
      if (!id || !access.canEdit) return;
      await notebooksService.update(id, data);
    },
    [id, access.canEdit]
  );

  // Add a new page
  const addPage = useCallback(
    async (input: CreatePageInput = {}) => {
      if (!id || !access.canEdit) return null;
      const newPage = await notebooksService.addPage(id, input);
      // Navigate to new page
      if (notebook) {
        setCurrentPageIndex(notebook.pages.length); // Will be at the end
      }
      return newPage;
    },
    [id, access.canEdit, notebook]
  );

  // Add page after current page
  const addPageAfterCurrent = useCallback(
    async () => {
      if (!currentPage) return addPage();
      return addPage({ insertAfterPageId: currentPage.id });
    },
    [addPage, currentPage]
  );

  // Update current page (debounced)
  const updateCurrentPage = useCallback(
    async (data: UpdatePageInput) => {
      if (!id || !access.canEdit || !currentPage) return;

      // Cancel previous timeout
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }

      pendingPageUpdate.current = { pageId: currentPage.id, data };

      // Debounce saves to avoid too many writes (1.5s for smooth drawing experience)
      saveTimeout.current = setTimeout(async () => {
        try {
          const pending = pendingPageUpdate.current;
          if (pending) {
            await notebooksService.updatePage(id, pending.pageId, pending.data);
            pendingPageUpdate.current = null;
          }
        } catch (err) {
          console.error('Failed to save page:', err);
        }
      }, 1500); // 1.5 second debounce for smooth drawing
    },
    [id, access.canEdit, currentPage]
  );

  // Immediate save (for explicit save actions)
  const saveCurrentPage = useCallback(
    async (data: UpdatePageInput) => {
      if (!id || !access.canEdit || !currentPage) return;

      // Clear any pending debounced save
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
        pendingPageUpdate.current = null;
      }

      await notebooksService.updatePage(id, currentPage.id, data);
    },
    [id, access.canEdit, currentPage]
  );

  // Delete current page
  const deleteCurrentPage = useCallback(
    async () => {
      if (!id || !access.canEdit || !currentPage || sortedPages.length <= 1) return;

      const currentIndex = currentPageIndex;
      await notebooksService.deletePage(id, currentPage.id);

      // Navigate to previous page or stay at current index if deleting last
      setCurrentPageIndex(Math.max(0, currentIndex - 1));
    },
    [id, access.canEdit, currentPage, currentPageIndex, sortedPages.length]
  );

  // Navigation
  const nextPage = useCallback(() => {
    if (currentPageIndex < sortedPages.length - 1) {
      setCurrentPageIndex(currentPageIndex + 1);
    }
  }, [currentPageIndex, sortedPages.length]);

  const previousPage = useCallback(() => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
  }, [currentPageIndex]);

  const goToPage = useCallback((index: number) => {
    if (index >= 0 && index < sortedPages.length) {
      setCurrentPageIndex(index);
    }
  }, [sortedPages.length]);

  // Reorder pages
  const reorderPages = useCallback(
    async (pageIds: string[]) => {
      if (!id || !access.canEdit) return;
      await notebooksService.reorderPages(id, pageIds);
    },
    [id, access.canEdit]
  );

  return {
    notebook,
    loading,
    error,
    // Access
    access,
    canEdit: access.canEdit,
    isOwner: access.isOwner,
    // Pages
    pages: sortedPages,
    currentPage,
    currentPageIndex,
    pageCount: sortedPages.length,
    // Notebook operations
    updateNotebook,
    // Page operations
    addPage,
    addPageAfterCurrent,
    updateCurrentPage,
    saveCurrentPage,
    deleteCurrentPage,
    reorderPages,
    // Navigation
    nextPage,
    previousPage,
    goToPage,
  };
};

// Sharing mutations (view-only for notebooks)
export const useInviteToNotebook = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      notebookId,
      input,
    }: {
      notebookId: string;
      input: InviteToNotebookInput;
    }) => notebooksService.inviteUser(notebookId, input, user!.uid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notebooks'] });
    },
  });
};

export const useRemoveNotebookSharedUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      notebookId,
      userEmail,
    }: {
      notebookId: string;
      userEmail: string;
    }) => notebooksService.removeSharedUser(notebookId, userEmail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notebooks'] });
    },
  });
};
